import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, CheckCircle, XCircle, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, verified, error
  const [message, setMessage] = useState('Completing sign in...');

  useEffect(() => {
    let timeoutId;
    let mounted = true;

    const handleCallback = async () => {
      try {
        // Check for error in URL params
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          console.error('Auth error from URL:', error, errorDescription);
          if (mounted) {
            setStatus('error');
            setMessage(errorDescription || 'Authentication failed');
            timeoutId = setTimeout(() => navigate('/auth'), 3000);
          }
          return;
        }

        // For email verification and password recovery, Supabase uses hash fragments
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        console.log('Auth callback - type:', type, 'has tokens:', !!accessToken);

        // Handle email verification
        if (type === 'signup' && accessToken && refreshToken) {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (sessionError) {
            console.error('Session error:', sessionError);
            if (mounted) {
              setStatus('error');
              setMessage('Failed to verify email. Please try again.');
              timeoutId = setTimeout(() => navigate('/auth'), 3000);
            }
            return;
          }
          
          if (data.session && mounted) {
            // Show special verified status for email verification
            setStatus('verified');
            setMessage('Your account has been verified!');
            toast.success('🎉 Account verified successfully!', {
              description: 'Welcome to FlipValue! Redirecting to your dashboard...',
              duration: 3000,
            });
            timeoutId = setTimeout(() => navigate('/dashboard'), 2500);
            return;
          }
        }

        // Handle password recovery
        if (type === 'recovery' && accessToken) {
          if (mounted) {
            setStatus('success');
            setMessage('Redirecting to reset password...');
            navigate('/reset-password' + window.location.hash);
          }
          return;
        }

        // Handle email change
        if (type === 'email_change' && accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (!sessionError && mounted) {
            setStatus('success');
            setMessage('Email updated successfully!');
            toast.success('Email updated!');
            timeoutId = setTimeout(() => navigate('/dashboard'), 1500);
            return;
          }
        }

        // For OAuth callbacks (Google, Apple), check for session
        // Give Supabase a moment to process the OAuth response
        const checkSession = async (attempt = 1) => {
          if (!mounted) return;
          
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Get session error:', sessionError);
            setStatus('error');
            setMessage('Authentication failed. Please try again.');
            timeoutId = setTimeout(() => navigate('/auth'), 3000);
            return;
          }
          
          if (session) {
            console.log('Session found, user:', session.user?.email);
            setStatus('success');
            setMessage('Signed in! Redirecting...');
            toast.success('Welcome to FlipValue!');
            timeoutId = setTimeout(() => navigate('/dashboard'), 1000);
          } else if (attempt < 5) {
            // Retry a few times with increasing delay
            console.log(`No session yet, retry ${attempt}/5`);
            timeoutId = setTimeout(() => checkSession(attempt + 1), 500 * attempt);
          } else {
            console.log('No session after retries');
            setStatus('error');
            setMessage('Could not complete sign in. Please try again.');
            timeoutId = setTimeout(() => navigate('/auth'), 2000);
          }
        };

        // Start checking for session
        await checkSession();
        
      } catch (err) {
        console.error('Callback error:', err);
        if (mounted) {
          setStatus('error');
          setMessage('Something went wrong. Please try again.');
          timeoutId = setTimeout(() => navigate('/auth'), 3000);
        }
      }
    };

    handleCallback();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-4">
      {status === 'processing' && (
        <>
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{message}</p>
        </>
      )}
      {status === 'verified' && (
        <div className="text-center animate-in fade-in-0 zoom-in-95 duration-300">
          <div className="relative inline-block mb-4">
            <CheckCircle className="w-16 h-16 text-green-500" />
            <PartyPopper className="w-6 h-6 text-yellow-500 absolute -top-1 -right-1 animate-bounce" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Account Verified! 🎉</h2>
          <p className="text-muted-foreground mb-4">{message}</p>
          <p className="text-sm text-muted-foreground">Redirecting to your dashboard...</p>
        </div>
      )}
      {status === 'success' && (
        <>
          <CheckCircle className="w-12 h-12 text-green-500" />
          <p className="text-foreground font-medium">{message}</p>
        </>
      )}
      {status === 'error' && (
        <>
          <XCircle className="w-12 h-12 text-red-500" />
          <p className="text-foreground font-medium">{message}</p>
          <p className="text-muted-foreground text-sm">Redirecting to sign in...</p>
        </>
      )}
    </div>
  );
}
